import { Directive, directive, ElementPart, PartInfo, PartType } from 'lit/directive.js'
import { fromEvent, merge, Subscription } from 'rxjs'
import { switchMap, take, takeUntil, tap } from 'rxjs/operators'

/** Fixed-position offsets (px) of a pinned element from the viewport's top-left. */
export type PinPosition = { left: number; top: number }

/** Below this movement (px), a pointer interaction is a tap, not a drag. */
const TAP_THRESHOLD_PX = 6

/**
 * Per-tab persistence, keyed by the consumer's own `storageKey` — placement is
 * an ephemeral convenience, not a durable preference. `state(...).session(...)`
 * (`@schmancy/state`) requires a namespace known at the type level; a `pin()`
 * call site supplies its key at runtime, so persistence goes straight through
 * `sessionStorage` instead, under the same per-key isolation the state
 * primitive itself provides.
 */
function readPosition(storageKey: string): PinPosition | null {
	try {
		const raw = sessionStorage.getItem(storageKey)
		if (!raw) return null
		const parsed = JSON.parse(raw) as unknown
		return parsed && typeof parsed === 'object' && 'left' in parsed && 'top' in parsed ? (parsed as PinPosition) : null
	} catch {
		return null
	}
}

function writePosition(storageKey: string, position: PinPosition): void {
	try {
		sessionStorage.setItem(storageKey, JSON.stringify(position))
	} catch {
		// sessionStorage unavailable (private mode, quota) — the drag still tracks the pointer live.
	}
}

export interface PinOptions {
	/** Element size (px) used to keep the drag inside the viewport. Defaults to the element's own measured box. */
	size?: number
	/** Minimum gap (px) kept from every viewport edge. Default 8. */
	inset?: number
	/** Fixed-position anchor used before any position is persisted, or after a viewport resize orphans the saved one. Default: 8px from the left, 1/3 down the viewport. */
	defaultPosition?: PinPosition
}

interface PinState {
	subscription: Subscription
	storageKey: string
	options: PinOptions
	restore: () => void
}

// Per-element state — keyed by element, not directive-instance fields, so a
// re-created directive instance (e.g. under `repeat`) reattaches cleanly.
const pinMap = new WeakMap<Element, PinState>()

class PinDirective extends Directive {
	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) {
			throw new Error('pin directive can only be used on elements')
		}
	}

	render(storageKey: string, options?: PinOptions) {
		void storageKey
		void options
		return undefined
	}

	update(part: ElementPart, [storageKey, options = {}]: [string, PinOptions?]) {
		const element = part.element as HTMLElement
		const existing = pinMap.get(element)

		if (existing) {
			if (existing.storageKey === storageKey && this.optionsEqual(existing.options, options)) {
				return undefined
			}
			this.cleanup(element)
		}

		const record: PinState = {
			subscription: new Subscription(),
			storageKey,
			options,
			restore: () => {},
		}
		pinMap.set(element, record)
		this.setup(element, record)
		return undefined
	}

	private optionsEqual(a: PinOptions, b: PinOptions): boolean {
		return (
			a.size === b.size &&
			a.inset === b.inset &&
			a.defaultPosition?.left === b.defaultPosition?.left &&
			a.defaultPosition?.top === b.defaultPosition?.top
		)
	}

	private setup(element: HTMLElement, record: PinState): void {
		const root = record.subscription
		const inset = record.options.inset ?? 8
		const size = record.options.size ?? (Math.max(element.offsetWidth, element.offsetHeight) || 56)
		const defaultPosition = record.options.defaultPosition ?? {
			left: inset,
			top: Math.round(window.innerHeight / 3),
		}

		const prevPosition = element.style.position
		const prevTouchAction = element.style.touchAction
		const prevUserSelect = element.style.userSelect
		record.restore = () => {
			element.style.position = prevPosition
			element.style.touchAction = prevTouchAction
			element.style.userSelect = prevUserSelect
		}

		element.style.position = 'fixed'
		element.style.touchAction = 'none'
		element.style.userSelect = 'none'

		const clampLeft = (left: number) => Math.min(Math.max(left, inset), window.innerWidth - size - inset)
		const clampTop = (top: number) => Math.min(Math.max(top, inset), window.innerHeight - size - inset)

		const saved = readPosition(record.storageKey)
		element.style.left = `${saved ? clampLeft(saved.left) : defaultPosition.left}px`
		element.style.top = `${saved ? clampTop(saved.top) : defaultPosition.top}px`

		root.add(
			fromEvent<PointerEvent>(element, 'pointerdown')
				.pipe(
					// switchMap (latest-wins): a second pointerdown before the previous
					// pointerup (multi-touch) tracks the newest pointer.
					switchMap(down => {
						const startX = down.clientX
						const startY = down.clientY
						let dragged = false

						return fromEvent<PointerEvent>(element, 'pointermove').pipe(
							tap(move => {
								const dx = move.clientX - startX
								const dy = move.clientY - startY
								if (!dragged && Math.hypot(dx, dy) >= TAP_THRESHOLD_PX) {
									dragged = true
									// Capture only once a real drag is confirmed: Chromium retargets
									// the pointerup-derived `click` to whichever element holds
									// capture at pointerup time, even if capture is released
									// synchronously inside the pointerup handler beforehand —
									// capturing eagerly on pointerdown swallows every tap's click.
									element.setPointerCapture(down.pointerId)
									element.setAttribute('data-pin-dragging', '')
								}
								element.style.left = `${clampLeft(move.clientX - size / 2)}px`
								element.style.top = `${clampTop(move.clientY - size / 2)}px`
							}),
							takeUntil(
								// pointerup AND pointercancel: touch devices fire pointercancel
								// (not pointerup) whenever the OS steals the gesture — an
								// incoming call, a notification pull-down, a second finger.
								merge(fromEvent<PointerEvent>(element, 'pointerup'), fromEvent<PointerEvent>(element, 'pointercancel')).pipe(
									take(1),
									tap(end => {
										element.removeAttribute('data-pin-dragging')
										if (element.hasPointerCapture(end.pointerId)) {
											element.releasePointerCapture(end.pointerId)
										}
										if (!dragged) return

										// A drag that moved past the tap threshold suppresses the
										// synthetic click the browser fires on this same pointer
										// session — otherwise every drag also fires the button.
										element.addEventListener(
											'click',
											clickEvent => {
												clickEvent.preventDefault()
												clickEvent.stopImmediatePropagation()
											},
											{ capture: true, once: true },
										)

										const left = parseFloat(element.style.left) || 0
										const top = parseFloat(element.style.top) || 0
										writePosition(record.storageKey, { left, top })
									}),
								),
							),
						)
					}),
				)
				.subscribe(),
		)
	}

	private cleanup(element: Element): void {
		const record = pinMap.get(element)
		if (!record) return
		record.subscription.unsubscribe()
		record.restore()
		pinMap.delete(element)
	}

	disconnected(part: ElementPart) {
		this.cleanup(part.element)
	}

	reconnected(part: ElementPart) {
		const element = part.element as HTMLElement
		const record = pinMap.get(element)
		if (record) {
			record.subscription = new Subscription()
			this.setup(element, record)
		}
	}
}

/**
 * Hold-and-drag-anywhere-in-the-viewport, position remembered per `storageKey`
 * across a refresh (`sessionStorage`). Ported from
 * `schmancy-navigation-rail-puck`'s pointer-capture drag mechanics — pointer
 * capture, `pointerup`/`pointercancel` termination, and a tap/drag threshold —
 * generalized so any element can opt in with a distinct storage key instead of
 * duplicating that pointer logic.
 *
 * The element is pinned as `position: fixed` and repositioned by direct style
 * writes during the drag (no re-render). A drag past the tap threshold
 * suppresses the click the browser synthesizes on release, so a draggable
 * button's own `click` handler fires only on a genuine tap.
 *
 * Applies only to an ElementPart (the tag itself), never a shadow-DOM-nested
 * child listener — the directive attaches `pointerdown` directly on the
 * element it decorates, so `event.target` is never retargeted the way it
 * would be listening from a host for events originating in the host's own
 * shadow tree.
 */
export const pin = directive(PinDirective)
